import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import BackButton from "../components/microComponents/BackButton";
import InfiniteScroll from "react-infinite-scroll-component";
import Skeleton from "react-loading-skeleton";
import PerformerApi from "../api/services/PerformerApi";
import { formatWeekRange } from "../utils/weekUtils";
import { errorToast } from "../utils/Toasts";
import Urls from "../api/Urls";
import useUserStore from "../stores/useUserStore";
import scrollToTop from "../components/ScrollTop/ScrollTop";

interface Performer {
  rank: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_profile_pic: string | null;
  total_answered: string;
  total_questions: string;
  total_correct_answers: string;
  quiz_count: number;
  accuracy: number;
}

/**
 * TopPerformerDetails page displays the list of top performers
 * for a specific week period
 */
const TopPerformerDetails: React.FC = () => {
  const { startDate, endDate } = useParams<{
    startDate: string;
    endDate: string;
  }>();
  
  const currentUser = useUserStore((state) => state.user);
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [currentUserData, setCurrentUserData] = useState<Performer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchPerformers(1);
    }
  }, [startDate, endDate]);

  const fetchPerformers = (page: number) => {
    setIsLoading(true);
    
    PerformerApi.getLeaderboardPerformance(startDate!, endDate!, page, {
      onSuccess: (response) => {
        setIsLoading(false);
        
        const data = response?.data;
        if (data) {
          // Set current user data (only on first page)
          if (page === 1 && data.current_user) {
            setCurrentUserData(data.current_user);
          }
          
          // Append leaderboard data
          if (data.leaderboard) {
            setPerformers(page === 1 ? data.leaderboard : [...performers, ...data.leaderboard]);
          }
          
          // Check pagination
          if (data.pagination) {
            const { current_page, last_page } = data.pagination;
            setCurrentPage(current_page);
            setHasMore(current_page < last_page);
          } else {
            setHasMore(false);
          }
        } else {
          if (page === 1) {
            setPerformers([]);
          }
          setHasMore(false);
        }
      },
      onError: (error) => {
        setIsLoading(false);
        console.error("Error fetching performers:", error);
        errorToast("Failed to load top performers");
        if (page === 1) {
          setPerformers([]);
        }
        setHasMore(false);
      },
    });
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      fetchPerformers(currentPage + 1);
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return rank;
  };

  const getRankClass = (rank: number) => {
    if (rank === 1) return "rank-1";
    if (rank === 2) return "rank-2";
    if (rank === 3) return "rank-3";
    return "";
  };

  const getProfilePicUrl = (pic: string | null) => {
    if (!pic) return null;
    return Urls.storagePath + pic;
  };

  const skeleton = (
    <div className="performers-list">
      {[...Array(5)].map((_, index) => (
        <div className="performer-item" key={index}>
          <div className="performer-rank">
            <Skeleton circle width={40} height={40} />
          </div>
          <div className="performer-avatar">
            <Skeleton circle width={50} height={50} />
          </div>
          <div className="performer-info" style={{ flex: 1 }}>
            <Skeleton width="60%" height={20} />
            <Skeleton width="80%" height={16} />
          </div>
          <div className="performer-stats">
            <Skeleton width={60} height={40} />
            <Skeleton width={60} height={40} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="Container pb-4 top-performer-details-page">
      <div className="home_container">
        <BackButton title="Top Performers" isSticky={true} />

        <div className="top-performer-details-content">
          <div className="details-header">
            <h2>Weekly Leaderboard</h2>
            {startDate && endDate && (
              <p className="week-period">{formatWeekRange(startDate, endDate)}</p>
            )}
          </div>

          {/* Current User Card */}
          {currentUserData && (
            <div className="current-user-section">
              <h3 className="section-title">Your Performance</h3>
              <div className={`current-user-card ${getRankClass(currentUserData.rank)}`}>
                <div className="user-rank-badge">
                  <span className="rank-number">{getRankIcon(currentUserData.rank)}</span>
                  <span className="rank-label">Rank</span>
                </div>
                
                <div className="user-profile">
                  <div className="user-avatar">
                    {getProfilePicUrl(currentUserData.user_profile_pic) ? (
                      <img src={getProfilePicUrl(currentUserData.user_profile_pic)!} alt={currentUserData.user_name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {currentUserData.user_name?.charAt(0).toUpperCase() || "U"}
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <h4 className="user-name">{currentUserData.user_name}</h4>
                    <p className="user-email">{currentUserData.user_email}</p>
                  </div>
                </div>

                <div className="user-stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">📝</div>
                    <div className="stat-content">
                      <span className="stat-value">{currentUserData.quiz_count}</span>
                      <span className="stat-label">Quizzes</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                      <span className="stat-value">{currentUserData.total_correct_answers}/{currentUserData.total_answered}</span>
                      <span className="stat-label">Correct</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">🎯</div>
                    <div className="stat-content">
                      <span className="stat-value">{currentUserData.accuracy.toFixed(1)}%</span>
                      <span className="stat-label">Accuracy</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-content">
                      <span className="stat-value">{currentUserData.total_answered}</span>
                      <span className="stat-label">Answered</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard Section */}
          <div className="leaderboard-section">
            <h3 className="section-title">Top Performers</h3>
            
            {isLoading && performers.length === 0 ? (
              skeleton
            ) : performers.length > 0 ? (
              <InfiniteScroll
                dataLength={performers.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={skeleton}
              >
                <div className="performers-list">
                  {performers.map((performer) => (
                    <div
                      key={performer.user_id}
                      className={`performer-item ${getRankClass(performer.rank)} ${
                        performer.user_id === currentUser?.id ? "is-current-user" : ""
                      }`}
                    >
                      <div className="performer-rank">
                        <span className="rank-badge">{getRankIcon(performer.rank)}</span>
                      </div>
                      
                      <div className="performer-avatar">
                        {getProfilePicUrl(performer.user_profile_pic) ? (
                          <img src={getProfilePicUrl(performer.user_profile_pic)!} alt={performer.user_name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {performer.user_name?.charAt(0).toUpperCase() || "U"}
                          </div>
                        )}
                      </div>

                      <div className="performer-info">
                        <h4 className="performer-name">
                          {performer.user_name}
                          {performer.user_id === currentUser?.id && (
                            <span className="you-badge">You</span>
                          )}
                        </h4>
                        <p className="performer-email">{performer.user_email}</p>
                        <div className="performer-meta">
                          <span>{performer.quiz_count} quiz{performer.quiz_count !== 1 ? "zes" : ""}</span>
                          <span>•</span>
                          <span>{performer.accuracy.toFixed(1)}% accuracy</span>
                        </div>
                      </div>

                      <div className="performer-stats">
                        <div className="stat">
                          <span className="stat-value">{performer.total_correct_answers}</span>
                          <span className="stat-label">Correct</span>
                        </div>
                        <div className="stat">
                          <span className="stat-value">{performer.total_answered}</span>
                          <span className="stat-label">Answered</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
            ) : (
              <div className="no-performers">
                <div className="empty-state-icon">🏆</div>
                <p>No performers found for this week.</p>
                <p className="hint">Be the first to participate!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopPerformerDetails;
